import React, { useState, useEffect } from 'react';
import { 
  Briefcase, CheckCircle, Clock, Calendar, TrendingUp, TrendingDown,
  DollarSign, Timer, ArrowUpRight, ArrowDownRight, Target,
  MapPin, Wrench, AlertCircle, ChevronRight
} from 'lucide-react';
import api from '../../services/api';
import { formatCurrency } from '../../utils/helpers';
import Loading from '../Loading';

const JobsSummary = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('month');
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/reports/jobs-summary?period=${period}`);
      setData(response.data);
    } catch (err) {
      console.error('Error loading jobs summary:', err);
      setError('Failed to load jobs data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Completed': 'bg-green-100 text-green-700',
      'Scheduled': 'bg-blue-100 text-blue-700',
      'In Progress': 'bg-yellow-100 text-yellow-700',
      'Quoted': 'bg-purple-100 text-purple-700',
      'Cancelled': 'bg-gray-100 text-gray-500',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) return <Loading />;

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
        <p className="text-red-800">{error}</p>
        <button 
          onClick={loadData}
          className="mt-3 text-sm text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  const {
    // Main metrics
    jobsCompleted = 0,
    previousJobsCompleted = 0,
    revenueFromJobs = 0,
    previousRevenue = 0,
    averageJobValue = 0,
    previousAverageJobValue = 0,
    
    // Efficiency metrics
    averageJobDuration = 0,
    revenuePerHour = 0,
    
    // Status breakdown
    statusBreakdown = [],
    
    // Top services
    topServices = [],
    
    // Upcoming jobs
    upcomingJobs = [],
    scheduledCount = 0,
    scheduledValue = 0,
    
    // Quote conversion
    quotesCreated = 0,
    quotesConverted = 0,
    conversionRate = 0,
  } = data || {};

  // Calculate changes
  const jobsChange = previousJobsCompleted > 0 
    ? ((jobsCompleted - previousJobsCompleted) / previousJobsCompleted * 100).toFixed(0)
    : 0;
  const revenueChange = previousRevenue > 0 
    ? ((revenueFromJobs - previousRevenue) / previousRevenue * 100).toFixed(0)
    : 0;
  const avgChange = previousAverageJobValue > 0
    ? ((averageJobValue - previousAverageJobValue) / previousAverageJobValue * 100).toFixed(0)
    : 0;

  // Max for service bars
  const maxServiceRevenue = Math.max(...topServices.map(s => s.revenue), 1);

  return (
    <div className="space-y-4">
      {/* Period Selector */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {['week', 'month', 'quarter', 'year'].map((p) => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              period === p
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {p === 'week' ? 'Week' : p === 'month' ? 'Month' : p === 'quarter' ? 'Quarter' : 'Year'}
          </button>
        ))}
      </div>

      {/* Main Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Jobs Completed */}
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between mb-1">
            <span className="text-blue-100 text-xs font-medium">Completed</span>
            <CheckCircle size={16} className="text-blue-200" />
          </div>
          <p className="text-2xl font-bold">{jobsCompleted}</p>
          <div className="flex items-center gap-1 mt-1">
            {Number(jobsChange) >= 0 ? (
              <ArrowUpRight size={14} className="text-green-300" />
            ) : (
              <ArrowDownRight size={14} className="text-red-300" />
            )}
            <span className={`text-xs ${Number(jobsChange) >= 0 ? 'text-green-200' : 'text-red-200'}`}>
              {Number(jobsChange) >= 0 ? '+' : ''}{jobsChange}% vs last {period}
            </span>
          </div>
        </div>

        {/* Revenue */}
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between mb-1">
            <span className="text-green-100 text-xs font-medium">Revenue</span>
            <DollarSign size={16} className="text-green-200" />
          </div>
          <p className="text-2xl font-bold">{formatCurrency(revenueFromJobs)}</p>
          <div className="flex items-center gap-1 mt-1">
            {Number(revenueChange) >= 0 ? (
              <ArrowUpRight size={14} className="text-green-300" />
            ) : (
              <ArrowDownRight size={14} className="text-red-300" />
            )}
            <span className={`text-xs ${Number(revenueChange) >= 0 ? 'text-green-200' : 'text-red-200'}`}>
              {Number(revenueChange) >= 0 ? '+' : ''}{revenueChange}%
            </span>
          </div>
        </div>

        {/* Average Job Value */}
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-purple-700 text-xs font-medium">Avg Job</span>
            <Target size={16} className="text-purple-500" />
          </div>
          <p className="text-2xl font-bold text-purple-900">{formatCurrency(averageJobValue)}</p>
          <div className="flex items-center gap-1 mt-1">
            {Number(avgChange) >= 0 ? (
              <ArrowUpRight size={14} className="text-green-500" />
            ) : (
              <ArrowDownRight size={14} className="text-red-500" />
            )}
            <span className={`text-xs ${Number(avgChange) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {Number(avgChange) >= 0 ? '+' : ''}{avgChange}%
            </span>
          </div>
        </div>

        {/* Revenue per Hour */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-amber-700 text-xs font-medium">$/Hour</span>
            <Timer size={16} className="text-amber-500" />
          </div>
          <p className="text-2xl font-bold text-amber-900">{formatCurrency(revenuePerHour)}</p>
          <p className="text-xs text-amber-600 mt-1">
            avg {averageJobDuration > 0 ? `${(averageJobDuration / 60).toFixed(1)}h` : '—'} per job
          </p>
        </div>
      </div>

      {/* Pipeline Overview */}
      <div className="grid grid-cols-1 gap-4">
        {/* Job Status Breakdown */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Job Pipeline</h3>
          
          {statusBreakdown.length > 0 ? (
            <div className="space-y-2">
              {statusBreakdown.map((status) => (
                <div 
                  key={status.status}
                  className="flex items-center justify-between p-2 rounded-lg bg-gray-50"
                >
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status.status)}`}>
                      {status.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600">{status.count} jobs</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatCurrency(status.value)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">No jobs in pipeline</p>
          )}
        </div>

        {/* Quote Conversion */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Quote Conversion</h3>
          
          <div className="flex items-center justify-center mb-4">
            <div className="relative w-32 h-32">
              {/* Background circle */}
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="#e5e7eb"
                  strokeWidth="12"
                  fill="none"
                />
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="#10b981"
                  strokeWidth="12"
                  fill="none"
                  strokeDasharray={`${conversionRate * 3.52} 352`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-bold text-gray-900">{conversionRate}%</span>
                <span className="text-xs text-gray-500">converted</span>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3 text-center">
            <div className="bg-purple-50 rounded-lg p-2">
              <p className="text-lg font-bold text-purple-900">{quotesCreated}</p>
              <p className="text-xs text-purple-600">Quotes Sent</p>
            </div>
            <div className="bg-green-50 rounded-lg p-2">
              <p className="text-lg font-bold text-green-900">{quotesConverted}</p>
              <p className="text-xs text-green-600">Won</p>
            </div>
          </div>
        </div>
      </div>

      {/* Top Services */}
      {topServices.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Top Services</h3>
          
          <div className="space-y-3">
            {topServices.slice(0, 5).map((service, index) => {
              const barWidth = (service.revenue / maxServiceRevenue * 100).toFixed(0);
              
              return (
                <div key={service.name || index}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Wrench size={14} className="text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">{service.name}</span>
                      <span className="text-xs text-gray-500">({service.count})</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatCurrency(service.revenue)}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full transition-all duration-500"
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Upcoming Jobs */}
      {upcomingJobs.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Coming Up</h3>
              <p className="text-xs text-gray-500">
                {scheduledCount} jobs • {formatCurrency(scheduledValue)} scheduled
              </p>
            </div>
            <a 
              href="/jobs?status=scheduled"
              className="text-xs text-primary-600 hover:text-primary-700"
            >
              View all
            </a>
          </div>
          
          <div className="space-y-2">
            {upcomingJobs.slice(0, 5).map((job, index) => (
              <div 
                key={job.id || index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {job.customerName}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Calendar size={12} />
                    <span>
                      {new Date(job.scheduledDate).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                    {job.city && (
                      <>
                        <MapPin size={12} />
                        <span className="truncate">{job.city}</span>
                      </>
                    )}
                  </div>
                </div>
                <span className="text-sm font-semibold text-gray-900 flex-shrink-0 ml-2">
                  {formatCurrency(job.quoteAmount)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {jobsCompleted === 0 && scheduledCount === 0 && (
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-8 text-center">
          <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 mb-1">No jobs yet</h3>
          <p className="text-sm text-gray-600">
            Once you start completing jobs, you'll see your summary here.
          </p>
          <a 
            href="/jobs"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 mt-3"
          >
            Go to Jobs
            <ChevronRight size={14} />
          </a>
        </div>
      )}
    </div>
  );
};

export default JobsSummary;